import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useIdleTimer } from "react-idle-timer";

import useAuth from "@hooks/useAuth";

import { IdleModal } from "@components/Modals/Idle";

export type IdleContextState = {
  inactivityModalOpen: boolean;
  remainingTime: number;
  onKeepSession: () => void;
};

export const IdleContext = createContext<IdleContextState>({
  inactivityModalOpen: false,
  remainingTime: 0,
  onKeepSession: () => {},
});

const INACTIVITY_MINUTES_LIMIT = 40;
const INACTIVITY_MODAL_SECONDS_DURATION = 60;

const timeout = INACTIVITY_MINUTES_LIMIT * 1000 * 60;
const promptBeforeIdle = INACTIVITY_MODAL_SECONDS_DURATION * 1000;

export const IdleContextProvider = ({ children }: { children: ReactNode }) => {
  const { onExpireSession } = useAuth();

  const [inactivityModalOpen, setInactivityModalOpen] = useState(false);
  const [remainingTime, setRemainingTime] = useState(
    INACTIVITY_MODAL_SECONDS_DURATION,
  );

  const onIdle = useCallback(() => {
    setInactivityModalOpen(false);
    onExpireSession();
  }, [onExpireSession]);

  const idleTimer = useIdleTimer({
    timeout,
    promptBeforeIdle,
    onActive: () => setInactivityModalOpen(false),
    onPrompt: () => setInactivityModalOpen(true),
    onIdle,
    crossTab: true,
    syncTimers: 200,
  });

  const onKeepSession = useCallback(() => idleTimer.activate(), [idleTimer]);

  useEffect(() => {
    if (!inactivityModalOpen) return;

    const updateTimer = () => {
      const remaining = idleTimer.getRemainingTime();

      if (remaining > promptBeforeIdle) return setInactivityModalOpen(false);
      setRemainingTime(Math.ceil(remaining / 1000));
    };

    updateTimer();
    const interval = setInterval(updateTimer, 500);
    return () => clearInterval(interval);
  }, [idleTimer, inactivityModalOpen]);

  return (
    <IdleContext.Provider
      value={{
        inactivityModalOpen,
        remainingTime,
        onKeepSession,
      }}
    >
      {children}
      <IdleModal />
    </IdleContext.Provider>
  );
};
